import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronsRight, MoveUpRight } from 'lucide-react';
import Seo from '../../components/Seo';
import AnimatedEyebrow from '../../components/AnimatedEyebrow';
import ImageOnlySkeletonLoader from '../../components/ImageOnlySkeletonLoader';
import PageLoader from '../../components/PageLoader';
import ShortcutsSection from '../../components/ShortcutsSection';
import { DEFAULT_SHORTCUTS } from '../../components/shortcutsData';
import { useI18n } from '../../context/I18nContext';
import { getImageUrl } from '../../lib/imageData';
import { localizePath } from '../../lib/localizedRoutes';
import { absoluteUrl, getSiteUrl } from '../../lib/seo';
import Link from 'next/link';

function isVisibleCategoryKey(key = '') {
  return String(key).trim() !== 'unclassified';
}

function toTypeStats(products = []) {
  const map = new Map();

  products.forEach((item) => {
    const typeKey = String(item?.productType || '').trim() || 'unclassified';
    if (!isVisibleCategoryKey(typeKey)) return;

    if (!map.has(typeKey)) {
      map.set(typeKey, {
        key: typeKey,
        count: 0,
        image: item?.images?.[0] || '',
      });
    }

    const current = map.get(typeKey);
    current.count += 1;
    if (!current.image && item?.images?.[0]) {
      current.image = item.images[0];
    }
  });

  return map;
}

async function fetchJsonOrThrow(url) {
  const response = await fetch(url);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error('Request failed');
  }
  return data;
}

export default function ProductCategoriesPage() {
  const { language, t } = useI18n();
  const siteUrl = getSiteUrl();
  const title = language === 'fr' ? 'Catégories de machines' : 'Machine categories';
  const description =
    language === 'fr'
      ? 'Explorez les familles de machines ETC et accédez rapidement aux catégories disponibles.'
      : 'Explore ETC machine families and quickly access the available categories.';
  const [products, setProducts] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    Promise.all([
      fetchJsonOrThrow(`/api/products?lang=${language}`),
      fetchJsonOrThrow('/api/product-types').catch(() => []),
    ])
      .then(([productsData, typeData]) => {
        if (!isMounted) return;
        setProducts(Array.isArray(productsData) ? productsData : []);
        setProductTypes((Array.isArray(typeData) ? typeData : []).filter((type) => isVisibleCategoryKey(type?.key)));
      })
      .catch(() => {
        if (!isMounted) return;
        setProducts([]);
        setProductTypes([]);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [language]);

  const categories = useMemo(() => {
    const statsByType = toTypeStats(products);
    return Array.from(statsByType.values())
      .map((item) => {
        const typeMeta = productTypes.find((type) => type.key === item.key);
        return {
          ...item,
          name: typeMeta?.labelI18n?.[language] || typeMeta?.label || item.key,
          image: typeMeta?.imageUrl || item.image || '',
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [products, productTypes, language]);
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: language === 'fr' ? 'Accueil' : 'Home',
        item: absoluteUrl('/', siteUrl),
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: language === 'fr' ? 'Machines' : 'Machines',
        item: absoluteUrl('/machines', siteUrl),
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: title,
        item: absoluteUrl('/machines/categories', siteUrl),
      },
    ],
  };
  if (isLoading) {
    return (
      <>
        <Seo
          title={title}
          description={description}
          jsonLd={[breadcrumbJsonLd]}
          keywords={{
            en: ['machine categories', 'forklift categories', 'warehouse equipment', 'material handling catalog', 'ETC Company'],
            fr: ['categories machines', 'categories chariots elevateurs', 'materiel de manutention', 'catalogue manutention', 'ETC Company'],
          }}
        />
        <PageLoader label={t('productsPage.hero.categories', 'Categories')} />
      </>
    );
  }

  return (
    <section className="relative pt-24 lg:pt-28 min-h-screen overflow-x-hidden bg-[#0a0a0c] text-white selection:bg-[var(--dash-main)] selection:text-black">
      <Seo
        title={title}
        description={description}
        jsonLd={[breadcrumbJsonLd]}
        keywords={{
          en: ['machine categories', 'forklift categories', 'warehouse equipment', 'material handling catalog', 'ETC Company'],
          fr: ['categories machines', 'categories chariots elevateurs', 'materiel de manutention', 'catalogue manutention', 'ETC Company'],
        }}
      />
      <div className="pointer-events-none absolute inset-0 z-0">
        <div className="absolute left-0 top-0 h-full w-full bg-[radial-gradient(circle_at_15%_25%,rgba(255,93,16,0.08)_0%,transparent_40%)]" />
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]" />
      </div>

      <main className="container relative z-10 mx-auto px-6 lg:px-10 pb-40">
        <header className="pb-14 md:pb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="text-center"
          >
            <AnimatedEyebrow className="mb-6 mx-auto w-fit">{t('productsPage.hero.categories')}</AnimatedEyebrow>
            <h1 className="text-4xl font-light uppercase leading-tight text-white sm:text-5xl md:text-6xl">
              {t('productsPage.categoriesHero.titleLine1')}
              <span className="block font-black bg-gradient-to-r from-orange-400 via-[var(--dash-main)] to-orange-700 bg-clip-text text-transparent">
                {t('productsPage.categoriesHero.titleLine2')}
              </span>
            </h1>
            <p className="mt-5 text-sm font-light leading-relaxed text-slate-400 md:text-base">
              {t('productsPage.categoriesHero.description')}
            </p>          </motion.div>
        </header>

        <section className="grid grid-cols-1 gap-12 md:grid-cols-2">
            {categories.length === 0 ? (
              <div className="col-span-full rounded-2xl bg-white/5 p-10 text-center">
                <p className="text-lg font-semibold">{t('productsPage.grid.noMatchesTitle')}</p>
                <p className="mt-2 text-sm text-gray-400">{t('productsPage.grid.noMatchesText')}</p>
              </div>
            ) : null}

            {categories.map((category, index) => {
              const shouldSpanFull = categories.length % 2 === 1 && index === categories.length - 1;
              const categoryHref = localizePath(
                `/machines?productType=${encodeURIComponent(category.key)}&productTypeLabel=${encodeURIComponent(category.name)}`,
                language,
              );
              return (
                <motion.article
                  key={category.key}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.08 }}
                  whileHover={{ y: -6 }}
                  className={`category-card group relative h-[400px] md:h-[500px] cursor-pointer overflow-hidden rounded-[2.5rem] border border-white/10 bg-[#121214] shadow-[0_24px_60px_-28px_rgba(0,0,0,0.7)] ${
                    shouldSpanFull ? 'md:col-span-2' : ''
                  }`}
                >
                <div className="absolute inset-0 z-0">
                  {getImageUrl(category.image) ? (
                    <ImageOnlySkeletonLoader
                      image={category.image}
                      mode="native"
                      alt={category.name}
                      containerClassName="h-full w-full"
                      imgClassName="h-full w-full object-cover opacity-100 transition-all duration-[1.5s] ease-out group-hover:scale-110 group-hover:opacity-90"
                      skeletonRadius="none"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-[#111] text-sm text-gray-400">
                      {t('common.noImage')}
                    </div>
                  )}
                  <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-1000 ease-in-out group-hover:translate-x-full" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/20 to-transparent opacity-80" />
                <div className="absolute inset-0 bg-[rgba(var(--dash-main-rgb),0.08)] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <div className="absolute inset-0 ring-1 ring-white/10 ring-inset" />

                <Link
                  href={categoryHref}
                  className="category-card-hitarea absolute inset-0 z-20 rounded-[2.5rem]"
                  aria-label={category.name}
                  title={category.name}
                />

                <div className="relative z-30 flex h-full flex-col items-start justify-between p-8 md:p-10 pointer-events-none">
                  <div className="flex w-full justify-start">
                    <span className="inline-flex items-center rounded-full bg-[var(--dash-main)] px-3.5 py-1.5 text-[12px] font-semibold tracking-[0.06em] text-white/95 shadow-[0_10px_24px_rgba(0,0,0,0.25)] transition-all duration-500 group-hover:shadow-[0_12px_28px_rgba(var(--dash-main-rgb),0.25)]">
                      {String(category.count).padStart(2, '0')} {t('productsPage.filters.products')}
                    </span>
                  </div>

                  <div className="w-full space-y-6 transition-transform duration-500 group-hover:-translate-y-2">
                    <h2 className="text-3xl font-[700] uppercase tracking-[-0.01em] leading-[1] text-white md:text-4xl">
                      {category.name}
                    </h2>
                    <div className="flex items-center justify-between border-t border-white/20 pt-6">
                      <span className="group/link flex items-center gap-2 text-sm font-bold text-zinc-300 transition-colors duration-300 group-hover:text-white">
                        <span className="relative">
                          {t('productsPage.grid.viewDetails')}
                          <span className="absolute -bottom-1.5 left-0 h-0.5 w-0 bg-[var(--dash-main)] transition-all duration-300 group-hover:w-full" />
                        </span>
                        <MoveUpRight size={16} className="transition-transform duration-300 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
                      </span>
                      <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[var(--dash-other)] text-black transition-all duration-500 group-hover:bg-[var(--dash-main)] group-hover:text-white group-hover:shadow-[0_0_40px_rgba(var(--dash-main-rgb),0.55)]">
                        <ChevronsRight size={20} strokeWidth={3} />
                      </span>
                    </div>
                  </div>
                </div>

                </motion.article>
              );
            })}
        </section>
      </main>
      <ShortcutsSection items={DEFAULT_SHORTCUTS} excludeCurrent />
      <style jsx>{`
        .category-card {
          transition: border-color 220ms ease, box-shadow 220ms ease;
        }

        .category-card:hover {
          border-color: rgba(var(--dash-main-rgb), 0.55);
          box-shadow: 0 24px 60px -28px rgba(0, 0, 0, 0.72), 0 0 0 1px rgba(var(--dash-main-rgb), 0.25);
        }

        .category-card-hitarea:focus-visible {
          outline: 2px solid #fff;
          outline-offset: -3px;
          box-shadow: 0 0 0 3px rgba(var(--dash-main-rgb), 0.5) inset;
        }
      `}</style>
    </section>
  );
}
